import React, { useState } from "react"
import { PageHeader } from "@/components/ui/page-header"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Spinner } from "@/components/ui/spinner"
import { StatusBadge } from "@/components/ui/status-badge"
import { AlertTriangle } from "lucide-react"
import { DemoCallComposer } from "./DemoCallComposer"
import { LiveCallPanel } from "./LiveCallPanel"
import { RecentDemoCalls } from "./RecentDemoCalls"
import {
  useDemoCallOptions,
  useDemoCallStatus,
  usePlaceDemoCall,
  useRecentDemoCalls,
} from "../hooks"
import type { DemoCallRequest } from "../types"

function errorMessage(error: unknown): string {
  const detail = (error as any)?.response?.data?.detail
  if (typeof detail === "string") return detail
  if ((error as any)?.response?.status === 403) {
    return "Your account can't place demo calls. Ask an admin for access to the demo organization."
  }
  return (error as any)?.message ?? "Something went wrong."
}

export function DemoCallsConsole() {
  const [activeCallId, setActiveCallId] = useState<string | null>(null)

  const optionsQuery = useDemoCallOptions()
  const recentQuery = useRecentDemoCalls()
  const statusQuery = useDemoCallStatus(activeCallId)
  const placeCall = usePlaceDemoCall()

  const handleSubmit = (request: DemoCallRequest) => {
    placeCall.mutate(request, {
      onSuccess: (response) => setActiveCallId(response.call_id),
    })
  }

  const handleReset = () => {
    setActiveCallId(null)
    placeCall.reset()
  }

  if (optionsQuery.isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Spinner />
      </div>
    )
  }

  if (optionsQuery.isError || !optionsQuery.data) {
    return (
      <div className="mx-auto max-w-7xl space-y-6 p-4 md:p-8">
        <PageHeader title="Demo calls" />
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Demo calls are unavailable</AlertTitle>
          <AlertDescription>{errorMessage(optionsQuery.error)}</AlertDescription>
        </Alert>
      </div>
    )
  }

  const options = optionsQuery.data

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-4 md:p-8">
      <PageHeader
        title="Demo calls"
        description="Put a live Pear agent on the phone with a prospect, right now."
        actions={
          <StatusBadge variant={options.retell_ready ? "success" : "muted"} dot>
            {options.retell_ready ? "Retell live" : "Retell off"}
          </StatusBadge>
        }
      />

      {!options.retell_ready && (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Voice provider is switched off</AlertTitle>
          <AlertDescription>
            Demo calls dial through Retell, which is disabled in this
            environment. Turn on RETELL_VOICE_PROVIDER_ENABLED (and confirm the
            API credentials) before running a demo.
          </AlertDescription>
        </Alert>
      )}

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,26rem)]">
        <DemoCallComposer
          options={options}
          submitting={placeCall.isPending}
          onSubmit={handleSubmit}
        />
        <div className="lg:sticky lg:top-6 lg:self-start">
          <LiveCallPanel
            status={statusQuery.data}
            dialing={placeCall.isPending}
            error={placeCall.isError ? errorMessage(placeCall.error) : null}
            onReset={handleReset}
          />
        </div>
      </div>

      <RecentDemoCalls
        calls={recentQuery.data ?? []}
        activeCallId={activeCallId}
        onSelect={setActiveCallId}
      />
    </div>
  )
}
